import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Search, Download, Upload, Eye, Edit, Trash2, X, FileText, Building2, ChevronDown } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import Wizard from '@/components/Wizard';
import CustomCheckbox from '@/components/Checkbox';
import Pagination from '@/components/Pagination';
import { usePermissions } from '@/contexts/PermissionsContext';

// Mapping pays -> indicatif téléphonique
const COUNTRY_CODES = {
  "Côte d'Ivoire": "+225",
  "Burkina Faso": "+226",
  "Mali": "+223",
  "Sénégal": "+221",
  "Ghana": "+233",
  "Togo": "+228",
  "Bénin": "+229",
  "Niger": "+227",
  "Guinée": "+224"
};

const Societes = () => {
  // ============ STATES ============
  const { hasPermission } = usePermissions();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [clientOperations, setClientOperations] = useState([]);
  const [loadingOperations, setLoadingOperations] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [operationsPage, setOperationsPage] = useState(1);
  const operationsPerPage = 10;
  
  // Pagination pour la liste principale des clients
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  // Secteurs d'activité
  const [secteurs, setSecteurs] = useState([]);
  const [showAddSecteur, setShowAddSecteur] = useState(false);
  const [newSecteur, setNewSecteur] = useState('');
  
  // Wizard state
  const [wizardStep, setWizardStep] = useState(1);
  
  // Toggle advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    nom: '',
    contact_principal_1: '',
    contact_principal_2: '',
    nom_contact: '',
    position: '',
    pays: 'Côte d\'Ivoire',
    secteur_activite: '',
    rccm: '',
    nif: '',
    site_web: '',
    adresse: '',
    ville: '',
    email: '',
    statut: 'actif',
    commentaires: ''
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    pays: 'all',
    secteur: 'all',
    statut: 'all'
  });
  
  // ============ AUTO-UPDATE INDICATIF ============
  useEffect(() => {
    // Mettre à jour l'indicatif automatiquement quand le pays change
    const indicatif = COUNTRY_CODES[formData.pays] || '+225';
    
    // Mettre à jour contact_principal_1 si vide ou commence par un ancien indicatif
    if (formData.contact_principal_1) {
      // Extraire le numéro sans indicatif
      const oldIndicatif = Object.values(COUNTRY_CODES).find(code => 
        formData.contact_principal_1.startsWith(code)
      );
      
      if (oldIndicatif && oldIndicatif !== indicatif) {
        const numero = formData.contact_principal_1.replace(oldIndicatif, '').trim();
        setFormData(prev => ({
          ...prev,
          contact_principal_1: `${indicatif} ${numero}`
        }));
      }
    }
    
    // Mettre à jour contact_principal_2 si présent
    if (formData.contact_principal_2) {
      const oldIndicatif = Object.values(COUNTRY_CODES).find(code => 
        formData.contact_principal_2.startsWith(code)
      );
      
      if (oldIndicatif && oldIndicatif !== indicatif) {
        const numero = formData.contact_principal_2.replace(oldIndicatif, '').trim();
        setFormData(prev => ({
          ...prev,
          contact_principal_2: `${indicatif} ${numero}`
        }));
      }
    }
  }, [formData.pays]);
  
  // ============ FETCH DATA ============
  useEffect(() => {
    fetchClients();
    fetchSecteurs();
  }, []);
  
  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSecteurs = async () => {
    try {
      const response = await api.get('/clients/secteurs/list');
      setSecteurs(response.data.secteurs || []);
    } catch (error) {
      console.error('Erreur chargement secteurs:', error);
    }
  };
  
  // ============ FORM HANDLERS ============
  const resetForm = () => {
    setFormData({
      nom: '',
      contact_principal_1: '',
      contact_principal_2: '',
      nom_contact: '',
      position: '',
      pays: 'Côte d\'Ivoire',
      secteur_activite: '',
      rccm: '',
      nif: '',
      site_web: '',
      adresse: '',
      ville: '',
      email: '',
      statut: 'actif',
      commentaires: ''
    });
    setWizardStep(1);
    setEditMode(false);
  };
  
  const handleAddSecteur = () => {
    if (newSecteur.trim()) {
      setSecteurs([...secteurs, newSecteur.trim()].sort());
      setFormData({ ...formData, secteur_activite: newSecteur.trim() });
      setNewSecteur('');
      setShowAddSecteur(false);
      toast.success('Secteur ajouté');
    }
  };
  
  const handleSubmit = async () => {
    try {
      if (editMode && selectedClient) {
        await api.put(`/clients/${selectedClient.id}`, formData);
        toast.success('Client modifié avec succès');
      } else {
        const response = await api.post('/clients', formData);
        toast.success(`Client créé: ${response.data.matricule}`);
      }
      
      fetchClients();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      
      // Gérer les erreurs Pydantic (tableau d'objets)
      if (Array.isArray(errorDetail)) {
        const errorMessages = errorDetail.map(err => {
          const field = err.loc ? err.loc[err.loc.length - 1] : 'champ';
          return `${field}: ${err.msg}`;
        }).join(', ');
        toast.error(`Erreur de validation: ${errorMessages}`);
      } 
      // Gérer les erreurs avec message
      else if (errorDetail?.message) {
        toast.error(errorDetail.message);
      }
      // Gérer les erreurs simples (string)
      else if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      }
      // Erreur générique
      else {
        toast.error('Erreur lors de l\'enregistrement');
      }
    }
  };
  
  const handleEdit = (client) => {
    setSelectedClient(client);
    setFormData({
      nom: client.nom || '',
      contact_principal_1: client.contact_principal_1 || '',
      contact_principal_2: client.contact_principal_2 || '',
      nom_contact: client.nom_contact || '',
      position: client.position || '',
      pays: client.pays || 'Côte d\'Ivoire',
      secteur_activite: client.secteur_activite || '',
      rccm: client.rccm || '',
      nif: client.nif || '',
      site_web: client.site_web || '',
      adresse: client.adresse || '',
      ville: client.ville || '',
      email: client.email || '',
      statut: client.statut || 'actif',
      commentaires: client.commentaires || ''
    });
    setEditMode(true);
    setDialogOpen(true);
  };
  
  const handleDelete = async (clientId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce client ?')) {
      return;
    }
    
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success('Client supprimé');
      fetchClients();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  const handleBulkDelete = async () => {
    try {
      await api.post('/clients/bulk-delete', { ids: selectedIds });
      toast.success(`${selectedIds.length} client(s) supprimé(s)`);
      setSelectedIds([]);
      setDeleteConfirmOpen(false);
      fetchClients();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  const handleViewDetails = async (client) => {
    setSelectedClient(client);
    setDetailsDialogOpen(true);
    setOperationsPage(1); // Réinitialiser à la page 1
    // Charger les opérations du client
    await fetchClientOperations(client.id);
  };

  const fetchClientOperations = async (clientId) => {
    setLoadingOperations(true);
    try {
      const response = await api.get('/operations');
      // Filtrer les opérations pour ce client
      const ops = response.data.filter(op => op.client_id === clientId);
      setClientOperations(ops);
    } catch (error) {
      console.error('Erreur chargement opérations:', error);
      setClientOperations([]);
    } finally {
      setLoadingOperations(false);
    }
  };
  
  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/clients/import/csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { summary, details } = response.data;
      
      toast.success(
        `Import terminé: ${summary.created} créé(s), ${summary.skipped} ignoré(s), ${summary.errors} erreur(s)`
      );
      
      if (details.errors.length > 0) {
        console.error('Erreurs d\'import:', details.errors);
      }
      
      fetchClients();
      event.target.value = '';  // Reset input
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      toast.error(errorDetail || 'Erreur lors de l\'import CSV');
    }
  };
  
  // ============ FILTERING ============
  const filteredClients = clients.filter(client => {
    let match = true;
    
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      match = match && (
        (client.nom?.toLowerCase().includes(searchLower)) ||
        (client.matricule?.toLowerCase().includes(searchLower)) ||
        (client.nom_contact?.toLowerCase().includes(searchLower)) ||
        (client.ville?.toLowerCase().includes(searchLower)) ||
        (client.rccm?.toLowerCase().includes(searchLower))
      );
    }
    
    if (filters.pays !== 'all') {
      match = match && client.pays === filters.pays;
    }
    
    if (filters.secteur !== 'all') {
      match = match && client.secteur_activite === filters.secteur;
    }
    
    if (filters.statut !== 'all') {
      match = match && client.statut === filters.statut;
    }
    
    return match;
  });

  // ============ PAGINATION LOGIC ============
  const totalPages = useMemo(() => Math.ceil(filteredClients.length / pageSize), [filteredClients.length, pageSize]);
  const paginatedClients = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredClients.slice(startIndex, endIndex);
  }, [filteredClients, currentPage, pageSize]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedIds([]);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    setSelectedIds([]);
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);
  
  // ============ WIZARD STEPS ============
  const wizardSteps = [
    { number: 1, title: 'Informations générales' },
    { number: 2, title: 'Informations administratives' },
    { number: 3, title: 'Informations complémentaires' }
  ];
  
  const canProceedStep1 = () => {
    return formData.nom && formData.contact_principal_1 && formData.nom_contact && formData.pays;
  };
  
  const canProceedStep2 = () => {
    return true; // Tous les champs de l'étape 2 sont optionnels
  };
  
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Building2 className="h-8 w-8 text-green-600" />
              Sociétés (Clients)
            </h1>
            <p className="text-gray-600 mt-1">Gestion des clients finaux</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/clients/export/csv`, '_blank')}
              variant="outline"
              className="border-gray-300"
            >
              <Download className="h-4 w-4 mr-2" />
              Exporter CSV
            </Button>
            <label htmlFor="import-csv">
              <Button
                variant="outline"
                className="border-gray-300"
                onClick={() => document.getElementById('import-csv').click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                Importer CSV
              </Button>
            </label>
            <input
              id="import-csv"
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleImportCSV}
            />
            {hasPermission('societes', 'create') && (
              <Button
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nouveau client
              </Button>
            )}
          </div>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-gray-900">{clients.length}</div>
              <div className="text-sm text-gray-600">Total clients</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-green-600">
                {clients.filter(c => c.statut === 'actif').length}
              </div>
              <div className="text-sm text-gray-600">Clients actifs</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-orange-600">
                {clients.filter(c => c.statut === 'inactif').length}
              </div>
              <div className="text-sm text-gray-600">Clients inactifs</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="text-2xl font-bold text-blue-600">
                {new Set(clients.map(c => c.secteur_activite).filter(Boolean)).size}
              </div>
              <div className="text-sm text-gray-600">Secteurs</div>
            </CardContent>
          </Card>
        </div>
        
        {/* Filters */}
        <Card>
          <CardHeader className="cursor-pointer" onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Search className="h-5 w-5" />
                Recherche et filtres
              </CardTitle>
              <Button variant="ghost" size="sm" className="gap-2">
                {showAdvancedFilters ? 'Masquer les filtres' : 'Afficher les filtres'}
                <ChevronDown className={`h-4 w-4 transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          
          {showAdvancedFilters && (
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Recherche</Label>
                  <Input
                    placeholder="Nom, matricule, contact..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Pays</Label>
                  <Select value={filters.pays} onValueChange={(value) => setFilters({ ...filters, pays: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="Côte d'Ivoire">Côte d'Ivoire</SelectItem>
                      <SelectItem value="Burkina Faso">Burkina Faso</SelectItem>
                      <SelectItem value="Mali">Mali</SelectItem>
                      <SelectItem value="Sénégal">Sénégal</SelectItem>
                      <SelectItem value="Ghana">Ghana</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Secteur d'activité</Label>
                  <Select value={filters.secteur} onValueChange={(value) => setFilters({ ...filters, secteur: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      {secteurs.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Statut</Label>
                  <Select value={filters.statut} onValueChange={(value) => setFilters({ ...filters, statut: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="actif">Actif</SelectItem>
                      <SelectItem value="inactif">Inactif</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          )}
        </Card>
        
        {/* Bulk Actions */}
        {selectedIds.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
            <span className="font-medium text-blue-900">
              {selectedIds.length} client(s) sélectionné(s)
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedIds([])}
              >
                <X className="h-4 w-4 mr-2" />
                Désélectionner
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setDeleteConfirmOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer
              </Button>
            </div>
          </div>
        )}
        
        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-200">
                  <tr>
                    <th className="text-left py-3 px-3 font-semibold text-gray-700 w-12">
                      <CustomCheckbox
                        checked={selectedIds.length === paginatedClients.length && paginatedClients.length > 0}
                        onChange={(checked) => {
                          if (checked) {
                            setSelectedIds(paginatedClients.map(c => c.id));
                          } else {
                            setSelectedIds([]);
                          }
                        }}
                      />
                    </th>
                    <th className="text-left py-3 px-3 font-semibold text-gray-700">Matricule</th>
                    <th className="text-left py-3 px-3 font-semibold text-gray-700">Nom</th>
                    <th className="text-left py-3 px-3 font-semibold text-gray-700">Contact</th>
                    <th className="text-left py-3 px-3 font-semibold text-gray-700">Nom Contact</th>
                    <th className="text-left py-3 px-3 font-semibold text-gray-700">Ville</th>
                    <th className="text-left py-3 px-3 font-semibold text-gray-700">Pays</th>
                    <th className="text-left py-3 px-3 font-semibold text-gray-700">Secteur</th>
                    <th className="text-left py-3 px-3 font-semibold text-gray-700">Statut</th>
                    <th className="text-left py-3 px-3 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedClients.map((client) => (
                    <tr key={client.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-3">
                        <CustomCheckbox
                          checked={selectedIds.includes(client.id)}
                          onChange={() => {
                            if (selectedIds.includes(client.id)) {
                              setSelectedIds(selectedIds.filter(id => id !== client.id));
                            } else {
                              setSelectedIds([...selectedIds, client.id]);
                            }
                          }}
                        />
                      </td>
                      <td className="py-3 px-3 font-medium text-gray-900">{client.matricule}</td>
                      <td className="py-3 px-3 font-medium text-gray-900">{client.nom}</td>
                      <td className="py-3 px-3 text-gray-700">{client.contact_principal_1}</td>
                      <td className="py-3 px-3 text-gray-700">{client.nom_contact}</td>
                      <td className="py-3 px-3 text-gray-700">{client.ville || '-'}</td>
                      <td className="py-3 px-3 text-gray-700">{client.pays}</td>
                      <td className="py-3 px-3 text-gray-700">{client.secteur_activite || '-'}</td>
                      <td className="py-3 px-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          client.statut === 'actif' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {client.statut}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(client)}
                            className="text-green-600 hover:text-green-700"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(client)}
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(client.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredClients.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  Aucun client trouvé
                </div>
              )}
            </div>

            {/* Pagination */}
            {filteredClients.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={filteredClients.length}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </CardContent>
        </Card>
        
        {/* Create/Edit Dialog with Wizard */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editMode ? 'Modifier le client' : 'Nouveau client'}
              </DialogTitle>
            </DialogHeader>
            
            <Wizard
              steps={wizardSteps}
              currentStep={wizardStep}
              onStepChange={setWizardStep}
              onNext={() => {
                if (wizardStep === 1 && canProceedStep1()) setWizardStep(2);
                else if (wizardStep === 2 && canProceedStep2()) setWizardStep(3);
              }}
              onPrevious={() => setWizardStep(wizardStep - 1)}
              onSubmit={handleSubmit}
              canProceed={
                (wizardStep === 1 && canProceedStep1()) ||
                (wizardStep === 2 && canProceedStep2()) ||
                wizardStep === 3
              }
            >
              {/* Étape 1: Informations générales */}
              {wizardStep === 1 && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nom">Nom de la société *</Label>
                    <Input
                      id="nom"
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="contact1">Contact principal 1 *</Label>
                      <Input
                        id="contact1"
                        placeholder={`${COUNTRY_CODES[formData.pays] || '+225'} 07 XX XX XX XX`}
                        value={formData.contact_principal_1}
                        onChange={(e) => setFormData({ ...formData, contact_principal_1: e.target.value })}
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="contact2">Contact principal 2</Label>
                      <Input
                        id="contact2"
                        placeholder={`${COUNTRY_CODES[formData.pays] || '+225'} 07 XX XX XX XX`}
                        value={formData.contact_principal_2}
                        onChange={(e) => setFormData({ ...formData, contact_principal_2: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nom_contact">Nom du contact *</Label>
                      <Input
                        id="nom_contact"
                        placeholder="Prénom NOM"
                        value={formData.nom_contact}
                        onChange={(e) => setFormData({ ...formData, nom_contact: e.target.value })}
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="position">Position / Fonction</Label>
                      <Input
                        id="position"
                        placeholder="Ex: Directeur des opérations"
                        value={formData.position}
                        onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="pays">Pays *</Label>
                      <Select value={formData.pays} onValueChange={(value) => setFormData({ ...formData, pays: value })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Côte d'Ivoire">Côte d'Ivoire</SelectItem>
                          <SelectItem value="Burkina Faso">Burkina Faso</SelectItem>
                          <SelectItem value="Mali">Mali</SelectItem>
                          <SelectItem value="Sénégal">Sénégal</SelectItem>
                          <SelectItem value="Ghana">Ghana</SelectItem>
                          <SelectItem value="Togo">Togo</SelectItem>
                          <SelectItem value="Bénin">Bénin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Secteur d'activité</Label>
                      {!showAddSecteur ? (
                        <div className="flex gap-2">
                          <Select value={formData.secteur_activite} onValueChange={(value) => setFormData({ ...formData, secteur_activite: value })}>
                            <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                            <SelectContent>
                              {secteurs.map(s => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setShowAddSecteur(true)}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Input
                            placeholder="Nouveau secteur"
                            value={newSecteur}
                            onChange={(e) => setNewSecteur(e.target.value)}
                          />
                          <Button type="button" size="sm" onClick={handleAddSecteur}>OK</Button>
                          <Button type="button" variant="outline" size="sm" onClick={() => setShowAddSecteur(false)}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Étape 2: Informations administratives */}
              {wizardStep === 2 && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="rccm">RCCM</Label>
                      <Input
                        id="rccm"
                        placeholder="Registre du Commerce"
                        value={formData.rccm}
                        onChange={(e) => setFormData({ ...formData, rccm: e.target.value })}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="nif">NIF</Label>
                      <Input
                        id="nif"
                        placeholder="Numéro d'Identification Fiscale"
                        value={formData.nif}
                        onChange={(e) => setFormData({ ...formData, nif: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="adresse">Adresse complète</Label>
                    <Input
                      id="adresse"
                      placeholder="Rue, quartier..."
                      value={formData.adresse}
                      onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="ville">Ville</Label>
                      <Input
                        id="ville"
                        placeholder="Ex: Abidjan"
                        value={formData.ville}
                        onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="contact@societe.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Étape 3: Informations complémentaires */}
              {wizardStep === 3 && (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="site_web">Site web</Label>
                    <Input
                      id="site_web"
                      placeholder="https://www.societe.com"
                      value={formData.site_web}
                      onChange={(e) => setFormData({ ...formData, site_web: e.target.value })}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="statut">Statut</Label>
                    <Select value={formData.statut} onValueChange={(value) => setFormData({ ...formData, statut: value })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="actif">Actif</SelectItem>
                        <SelectItem value="inactif">Inactif</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="commentaires">Commentaires / Notes</Label>
                    <textarea
                      id="commentaires"
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Notes internes..."
                      value={formData.commentaires}
                      onChange={(e) => setFormData({ ...formData, commentaires: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </Wizard>
          </DialogContent>
        </Dialog>
        
        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmer la suppression</DialogTitle>
            </DialogHeader>
            <p>Êtes-vous sûr de vouloir supprimer {selectedIds.length} client(s) ?</p>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Annuler</Button>
              <Button variant="destructive" onClick={handleBulkDelete}>Supprimer</Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Details Dialog */}
        {selectedClient && (
          <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Détails du client - {selectedClient.nom}</DialogTitle>
              </DialogHeader>
              
              <Tabs defaultValue="details" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="details">Informations</TabsTrigger>
                  <TabsTrigger value="operations">Opérations ({clientOperations.length})</TabsTrigger>
                </TabsList>
                
                <TabsContent value="details" className="mt-4">
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Matricule</p>
                    <p className="font-medium">{selectedClient.matricule}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Nom</p>
                    <p className="font-medium">{selectedClient.nom}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Contact principal 1</p>
                    <p className="font-medium">{selectedClient.contact_principal_1}</p>
                  </div>
                  {selectedClient.contact_principal_2 && (
                    <div>
                      <p className="text-sm text-gray-600">Contact principal 2</p>
                      <p className="font-medium">{selectedClient.contact_principal_2}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600">Nom du contact</p>
                    <p className="font-medium">{selectedClient.nom_contact}</p>
                  </div>
                  {selectedClient.position && (
                    <div>
                      <p className="text-sm text-gray-600">Position</p>
                      <p className="font-medium">{selectedClient.position}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600">Pays</p>
                    <p className="font-medium">{selectedClient.pays}</p>
                  </div>
                  {selectedClient.secteur_activite && (
                    <div>
                      <p className="text-sm text-gray-600">Secteur d'activité</p>
                      <p className="font-medium">{selectedClient.secteur_activite}</p>
                    </div>
                  )}
                  {selectedClient.ville && (
                    <div>
                      <p className="text-sm text-gray-600">Ville</p>
                      <p className="font-medium">{selectedClient.ville}</p>
                    </div>
                  )}
                  {selectedClient.email && (
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-medium">{selectedClient.email}</p>
                    </div>
                  )}
                  {selectedClient.rccm && (
                    <div>
                      <p className="text-sm text-gray-600">RCCM</p>
                      <p className="font-medium">{selectedClient.rccm}</p>
                    </div>
                  )}
                  {selectedClient.nif && (
                    <div>
                      <p className="text-sm text-gray-600">NIF</p>
                      <p className="font-medium">{selectedClient.nif}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600">Statut</p>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                      selectedClient.statut === 'actif' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {selectedClient.statut}
                    </span>
                  </div>
                </div>
                
                {selectedClient.commentaires && (
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Commentaires</p>
                    <p className="text-gray-900 whitespace-pre-wrap">{selectedClient.commentaires}</p>
                  </div>
                )}
              </div>
                </TabsContent>
                
                <TabsContent value="operations" className="mt-4">
                  <div className="space-y-4">
                    {loadingOperations ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                      </div>
                    ) : clientOperations.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        Aucune opération trouvée pour ce client
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left py-2 px-3 font-semibold text-gray-700">Matricule</th>
                              <th className="text-left py-2 px-3 font-semibold text-gray-700">Date</th>
                              <th className="text-left py-2 px-3 font-semibold text-gray-700">Trajet</th>
                              <th className="text-left py-2 px-3 font-semibold text-gray-700">Marchandise</th>
                              <th className="text-left py-2 px-3 font-semibold text-gray-700">Chauffeur</th>
                              <th className="text-left py-2 px-3 font-semibold text-gray-700">Statut</th>
                            </tr>
                          </thead>
                          <tbody>
                            {clientOperations
                              .slice((operationsPage - 1) * operationsPerPage, operationsPage * operationsPerPage)
                              .map((op, idx) => (
                              <tr key={idx} className="border-b hover:bg-gray-50">
                                <td className="py-2 px-3 font-mono text-xs">{op.matricule || '-'}</td>
                                <td className="py-2 px-3">{op.date_operation ? new Date(op.date_operation).toLocaleDateString('fr-FR') : '-'}</td>
                                <td className="py-2 px-3">{op.ville_depart && op.ville_destination ? `${op.ville_depart} → ${op.ville_destination}` : '-'}</td>
                                <td className="py-2 px-3">{op.type_marchandise || '-'}</td>
                                <td className="py-2 px-3">{op.chauffeur || '-'}</td>
                                <td className="py-2 px-3">
                                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                                    op.statut === 'Terminé' ? 'bg-green-100 text-green-700' :
                                    op.statut === 'En cours' ? 'bg-blue-100 text-blue-700' :
                                    op.statut === 'Nouveau' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {op.statut}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        
                        {/* Pagination */}
                        {clientOperations.length > operationsPerPage && (
                          <div className="flex items-center justify-between mt-4 pt-4 border-t">
                            <div className="text-sm text-gray-600">
                              Affichage {((operationsPage - 1) * operationsPerPage) + 1} à {Math.min(operationsPage * operationsPerPage, clientOperations.length)} sur {clientOperations.length} opérations
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setOperationsPage(operationsPage - 1)}
                                disabled={operationsPage === 1}
                              >
                                Précédent
                              </Button>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">
                                  Page {operationsPage} sur {Math.ceil(clientOperations.length / operationsPerPage)}
                                </span>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setOperationsPage(operationsPage + 1)}
                                disabled={operationsPage >= Math.ceil(clientOperations.length / operationsPerPage)}
                              >
                                Suivant
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
        )}
    </div>
  );
};

export default Societes;
